import React, { memo } from 'react';
import { Box, Typography, Link, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

interface MessageFormatterProps {
  text: string;
  isUser: boolean;
}

const formatMessage = (text: string, navigate?: (path: string) => void): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;

  // Regex patterns for different formatting
  const patterns = [
    { regex: /\*\*(.*?)\*\*/g, type: 'bold' },
    { regex: /\*(.*?)\*/g, type: 'italic' },
    { regex: /`(.*?)`/g, type: 'code' },
    { regex: /\[([^\]]+)\]\(([^)]+)\)/g, type: 'link' },
    // Payment URL with query params (special styling)
    { regex: /(https?:\/\/[^\s)]*\/paymentsv2\/Payment\/CREATE\/4\?[^\s)]+)/g, type: 'payment_url' },
    // Plain URL autolink (http/https/www)
    { regex: /(https?:\/\/[^\s)]+|www\.[^\s)]+)/g, type: 'url' },
    { regex: /^### (.*$)/gm, type: 'h3' },
    { regex: /^## (.*$)/gm, type: 'h2' },
    { regex: /^# (.*$)/gm, type: 'h1' },
    { regex: /^- (.*$)/gm, type: 'list' },
    { regex: /^\d+\. (.*$)/gm, type: 'numbered' },
  ];

  let processedText = text;
  const replacements: Array<{ start: number; end: number; type: string; content: string; link?: string }> = [];

  // Find all matches
  patterns.forEach(({ regex, type }) => {
    let match;
    while ((match = regex.exec(text)) !== null) {
      replacements.push({
        start: match.index,
        end: match.index + match[0].length,
        type,
        content: match[1] || match[0],
        link: match[2],
      });
    }
  });

  // Sort by start position
  replacements.sort((a, b) => a.start - b.start);

  // Process text
  let lastIndex = 0;
  replacements.forEach((replacement) => {
    if (replacement.start >= lastIndex) {
      // Add text before replacement
      if (replacement.start > lastIndex) {
        const beforeText = text.slice(lastIndex, replacement.start);
        if (beforeText) {
          parts.push(
            <Typography key={`text-${lastIndex}`} component="span" variant="body2">
              {beforeText}
            </Typography>
          );
        }
      }

      // Add formatted content
      switch (replacement.type) {
        case 'bold':
          parts.push(
            <Typography key={`bold-${replacement.start}`} component="span" variant="body2" fontWeight="bold">
              {replacement.content}
            </Typography>
          );
          break;
        case 'italic':
          parts.push(
            <Typography key={`italic-${replacement.start}`} component="span" variant="body2" fontStyle="italic">
              {replacement.content}
            </Typography>
          );
          break;
        case 'code':
          parts.push(
            <Box
              key={`code-${replacement.start}`}
              component="code"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                padding: '2px 4px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
            >
              {replacement.content}
            </Box>
          );
          break;
        case 'link':
          parts.push(
            <Link
              key={`link-${replacement.start}`}
              href={replacement.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: 'underline' }}
            >
              {replacement.content}
            </Link>
          );
          break;
        case 'payment_url': {
          const href = replacement.content.startsWith('http')
            ? replacement.content
            : `http://${replacement.content}`;
          
          // Check if it's an internal link (localhost or app.ledgerx.biz)
          const isInternalLink = href.includes('localhost:3000') || href.includes('app.ledgerx.biz');
          
          if (isInternalLink && navigate) {
            // Extract path from URL for internal navigation
            const url = new URL(href);
            const path = url.pathname + url.search;
            
            // Create user-friendly button text
            let buttonText = '💳 Create Payment';
            
            parts.push(
              <Link
                key={`payment-url-${replacement.start}`}
                component="button"
                onClick={() => navigate(path)}
                sx={{ 
                  textDecoration: 'none',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'inline-block',
                  margin: '4px 0',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {buttonText}
              </Link>
            );
          } else {
            // External link
            parts.push(
              <Link
                key={`payment-url-${replacement.start}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  textDecoration: 'none',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  display: 'inline-block',
                  margin: '4px 0',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                💳 Create Payment
              </Link>
            );
          }
          break;
        }
        case 'url': {
          const href = replacement.content.startsWith('http')
            ? replacement.content
            : `http://${replacement.content}`;
          parts.push(
            <Link
              key={`url-${replacement.start}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: 'underline' }}
            >
              {replacement.content}
            </Link>
          );
          break;
        }
        case 'h1':
          parts.push(
            <Typography key={`h1-${replacement.start}`} variant="h6" component="div" sx={{ mt: 1, mb: 0.5 }}>
              {replacement.content}
            </Typography>
          );
          break;
        case 'h2':
          parts.push(
            <Typography key={`h2-${replacement.start}`} variant="subtitle1" component="div" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
              {replacement.content}
            </Typography>
          );
          break;
        case 'h3':
          parts.push(
            <Typography key={`h3-${replacement.start}`} variant="subtitle2" component="div" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
              {replacement.content}
            </Typography>
          );
          break;
        case 'list':
          parts.push(
            <Box key={`list-${replacement.start}`} component="div" sx={{ ml: 2 }}>
              • {replacement.content}
            </Box>
          );
          break;
        case 'numbered':
          parts.push(
            <Box key={`numbered-${replacement.start}`} component="div" sx={{ ml: 2 }}>
              {replacement.content}
            </Box>
          );
          break;
        default:
          parts.push(
            <Typography key={`default-${replacement.start}`} component="span" variant="body2">
              {replacement.content}
            </Typography>
          );
      }

      lastIndex = replacement.end;
    }
  });

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      parts.push(
        <Typography key={`text-${lastIndex}`} component="span" variant="body2">
          {remainingText}
        </Typography>
      );
    }
  }

  return parts.length > 0 ? parts : [text];
};

export const MessageFormatter = memo<MessageFormatterProps>(({ text, isUser }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  if (isUser) {
    // Simple formatting for user messages
    return (
      <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
        {text}
      </Typography>
    );
  }

  // Rich formatting for bot messages
  const formattedContent = formatMessage(text, navigate);

  return (
    <Box sx={{ wordBreak: 'break-word', lineHeight: 1.6 }}>
      {formattedContent}
    </Box>
  );
});

MessageFormatter.displayName = 'MessageFormatter';
